import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const restrictionCache = new CacheManager('VueCache', 'cache', {
    maxAge: 24 * 60 * 60 * 1000,
    memoryCacheTTL: 30 * 60 * 1000,
    logCategory: 'Restriction',
    keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.restrictions.`,
});

export async function getProductTreeRestrictions(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = restrictionCache.getFromMemory('productTreeRestrictions');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('PRODUCTTREERESTRICTIONS');
            if (legacyData) {
                log('info', 'Restriction', '📦 Seeding Vue cache from legacy storage (PRODUCTTREERESTRICTIONS)');
                await restrictionCache.put('productTreeRestrictions', legacyData);
                restrictionCache.setInMemory('productTreeRestrictions', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'Restriction', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await restrictionCache.get('productTreeRestrictions');
        if (cached && !forceRefresh) {
            restrictionCache.setInMemory('productTreeRestrictions', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'Restriction', '📴 Offline: Returning cached product tree restrictions');
                return cached;
            }

            setTimeout(() => refreshProductTreeRestrictionsInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'Restriction', '📴 Offline: No cached product tree restrictions available');
            return [];
        }

        return await fetchProductTreeRestrictionsFromAPI();
    } catch (error) {
        log('error', 'Restriction', `❌ Error getting product tree restrictions: ${error.message}`);

        try {
            const fallback = await restrictionCache.get('productTreeRestrictions');
            if (fallback) {
                log('info', 'Restriction', '💾 Using fallback cached product tree restrictions');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'Restriction', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

export async function getProductTreeCrossRestrictions(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = restrictionCache.getFromMemory('productTreeCrossRestrictions');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('PT_CROSS_RESTRICTIONS');
            if (legacyData) {
                log('info', 'Restriction', '📦 Seeding Vue cache from legacy storage (PT_CROSS_RESTRICTIONS)');
                await restrictionCache.put('productTreeCrossRestrictions', legacyData);
                restrictionCache.setInMemory('productTreeCrossRestrictions', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'Restriction', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await restrictionCache.get('productTreeCrossRestrictions');
        if (cached && !forceRefresh) {
            restrictionCache.setInMemory('productTreeCrossRestrictions', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'Restriction', '📴 Offline: Returning cached product tree cross restrictions');
                return cached;
            }

            setTimeout(() => refreshProductTreeCrossRestrictionsInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'Restriction', '📴 Offline: No cached product tree cross restrictions available');
            return [];
        }

        return await fetchProductTreeCrossRestrictionsFromAPI();
    } catch (error) {
        log('error', 'Restriction', `❌ Error getting product tree cross restrictions: ${error.message}`);

        try {
            const fallback = await restrictionCache.get('productTreeCrossRestrictions');
            if (fallback) {
                log('info', 'Restriction', '💾 Using fallback cached product tree cross restrictions');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'Restriction', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

async function fetchProductTreeRestrictionsFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Core/ProductTreeRestrictions'), { method: 'GET' }, 'token');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const restrictions = await response.json();
        await restrictionCache.put('productTreeRestrictions', restrictions);
        restrictionCache.setInMemory('productTreeRestrictions', restrictions);

        log('success', 'Restriction', `✅ Fetched and cached ${restrictions.length} product tree restrictions`);
        return restrictions;
    } catch (error) {
        log('error', 'Restriction', `❌ Failed to fetch product tree restrictions from API: ${error.message}`);
        throw error;
    }
}

async function fetchProductTreeCrossRestrictionsFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Core/ProductTreeCrossRestrictions'), { method: 'GET' }, 'token');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const crossRestrictions = await response.json();
        await restrictionCache.put('productTreeCrossRestrictions', crossRestrictions);
        restrictionCache.setInMemory('productTreeCrossRestrictions', crossRestrictions);

        log('success', 'Restriction', `✅ Fetched and cached ${crossRestrictions.length} product tree cross restrictions`);
        return crossRestrictions;
    } catch (error) {
        log('error', 'Restriction', `❌ Failed to fetch product tree cross restrictions from API: ${error.message}`);
        throw error;
    }
}

async function refreshProductTreeRestrictionsInBackground() {
    try {
        await fetchProductTreeRestrictionsFromAPI();
        log('success', 'Restriction', '🔄 Background refresh of product tree restrictions completed');
    } catch (error) {
        log('warn', 'Restriction', `⚠️ Background refresh failed: ${error.message}`);
    }
}

async function refreshProductTreeCrossRestrictionsInBackground() {
    try {
        await fetchProductTreeCrossRestrictionsFromAPI();
        log('success', 'Restriction', '🔄 Background refresh of product tree cross restrictions completed');
    } catch (error) {
        log('warn', 'Restriction', `⚠️ Background refresh failed: ${error.message}`);
    }
}

export async function refreshRestrictions() {
    restrictionCache.clearMemory();
    await restrictionCache.clear();
}
